"use client"

import * as React from "react"
import { Grid, Plus, Trash2, Pencil, Sparkles, Store, Lock, Info, ShieldAlert } from "lucide-react"
import { TabsContent } from "@/components/ui/tabs"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { AppIcon } from "@/components/desk/AppIcon"
import { toast } from "@/lib/toast"

export interface AppsSettingsTabProps {
  installedApps: any[]
  handleToggleAppStatus: (id: string) => void
  handleUninstallApp: (id: string) => void
  handleInstallApp?: (app: any) => void
  handleRenameApp?: (appId: string, newName: string) => void
  handleOpenMarketplaceTab?: () => void
  setSettingsTab: (tab: string) => void
  isCompanyAdmin?: boolean
}

export function AppsSettingsTab({
  installedApps,
  handleToggleAppStatus,
  handleUninstallApp,
  handleInstallApp,
  handleRenameApp,
  handleOpenMarketplaceTab,
  setSettingsTab,
  isCompanyAdmin = true,
}: AppsSettingsTabProps) {
  const [showAddAppDropdown, setShowAddAppDropdown] = React.useState(false)
  const [showNewAppModal, setShowNewAppModal] = React.useState(false)
  const [editingApp, setEditingApp] = React.useState<any>(null)
  const [appToRename, setAppToRename] = React.useState<{ id: string; name: string; isCustom?: boolean } | null>(null)
  const [renameInputValue, setRenameInputValue] = React.useState("")
  const [appToDelete, setAppToDelete] = React.useState<{ id: string; name: string; isCustom?: boolean } | null>(null)
  const [newAppName, setNewAppName] = React.useState("")
  const [newAppCategory, setNewAppCategory] = React.useState("Utilitários")
  const [newAppIconUrl, setNewAppIconUrl] = React.useState("")
  const [newAppIframeUrl, setNewAppIframeUrl] = React.useState("")
  const [customAppsList, setCustomAppsList] = React.useState<any[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_custom_apps")
        if (saved) return JSON.parse(saved)
      } catch (e) { }
    }
    return []
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_custom_apps", JSON.stringify(customAppsList))
      } catch (e) { }
    }
  }, [customAppsList])

  const handleOpenRenameModal = (appId: string, currentName: string, isCustom = false) => {
    setAppToRename({ id: appId, name: currentName, isCustom })
    setRenameInputValue(currentName)
  }

  const handleSaveRename = (e: React.FormEvent) => {
    e.preventDefault()
    if (!appToRename || !renameInputValue.trim()) return

    const trimmedName = renameInputValue.trim()

    if (appToRename.isCustom) {
      setCustomAppsList((prev) =>
        prev.map((a) => (a.id === appToRename.id ? { ...a, name: trimmedName } : a))
      )
    }

    if (handleRenameApp) {
      handleRenameApp(appToRename.id, trimmedName)
    } else {
      toast.success("Nome do aplicativo atualizado", {
        description: `O aplicativo agora será exibido como "${trimmedName}" no workspace.`,
      })
    }

    setAppToRename(null)
  }

  return (
    <TabsContent value="apps-list-tab" className="outline-none space-y-6">
      <div className="flex flex-row items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2 font-sans">
            <Grid className="size-5 text-[#004EB1]" />
            Gerenciamento de Aplicativos
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
            Gerencie, renomeie e ative os aplicativos disponíveis para todos os membros deste workspace.
          </p>
        </div>

        {/* Botão de Adicionar Aplicativo com Dropdown (Apenas Owner/Admin da Empresa) */}
        {isCompanyAdmin && (
          <div className="relative">
            <Button
              size="sm"
              onClick={() => setShowAddAppDropdown(!showAddAppDropdown)}
              className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-3 flex items-center gap-1 cursor-pointer font-sans"
            >
              <Plus className="size-3.5" />
              <span>Adicionar App</span>
            </Button>

            {showAddAppDropdown && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowAddAppDropdown(false)} />
                <div className="absolute right-0 mt-1.5 w-52 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-lg py-1.5 z-40 animate-in fade-in slide-in-from-top-1 duration-150 text-xs font-sans">
                  <button
                    onClick={() => {
                      setShowAddAppDropdown(false)
                      if (handleOpenMarketplaceTab) {
                        handleOpenMarketplaceTab()
                      }
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300 font-semibold cursor-pointer"
                  >
                    🛒 Instalar do Marketplace
                  </button>
                  <button
                    onClick={() => {
                      setShowAddAppDropdown(false)
                      setShowNewAppModal(true)
                      setNewAppName("")
                      setNewAppCategory("Utilitários")
                      setNewAppIconUrl("https://cdn.boldworkspace.com.br/icons/app-default.png")
                      setNewAppIframeUrl("")
                      setEditingApp(null)
                    }}
                    className="w-full text-left px-3.5 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300 font-semibold cursor-pointer border-t border-zinc-100 dark:border-zinc-800/80"
                  >
                    🌐 Cadastrar App Customizado (URL)
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Formulário de Criação / Edição Completa de App Customizado */}
      {(showNewAppModal || editingApp) && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (editingApp) {
              setCustomAppsList((prev) =>
                prev.map((a) =>
                  a.id === editingApp.id
                    ? {
                      ...a,
                      name: newAppName,
                      category: newAppCategory,
                      iconUrl: newAppIconUrl || "https://cdn.boldworkspace.com.br/icons/app-default.png",
                      iframeUrl: newAppIframeUrl,
                    }
                    : a
                )
              )
              toast.success("Aplicativo Atualizado", { description: "As alterações foram salvas com sucesso." })
            } else {
              const newId = `custom-app-${Date.now()}`
              const newA = {
                id: newId,
                name: newAppName,
                category: newAppCategory,
                iconUrl: newAppIconUrl || "https://cdn.boldworkspace.com.br/icons/app-default.png",
                iframeUrl: newAppIframeUrl,
                isCustom: true,
              }
              setCustomAppsList((prev) => [...prev, newA])
              if (typeof handleInstallApp === "function") {
                handleInstallApp(newA)
              }
              toast.success("Aplicativo Criado e Instalado", { description: "Seu aplicativo web já está disponível e ativo no workspace." })
            }
            setShowNewAppModal(false)
            setEditingApp(null)
          }}
          className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4 bg-zinc-50 dark:bg-zinc-950/50 space-y-4 animate-in fade-in duration-200 font-sans"
        >
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-zinc-850 dark:text-zinc-200">
              {editingApp ? "Editar Aplicativo Customizado" : "Cadastrar Novo Aplicativo Customizado"}
            </h4>
            <button
              type="button"
              onClick={() => {
                setShowNewAppModal(false)
                setEditingApp(null)
              }}
              className="text-zinc-400 hover:text-zinc-600 text-xs font-bold"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400">Nome do App</label>
              <input
                type="text"
                placeholder="Ex: ERP de Vendas"
                value={newAppName}
                onChange={(e) => setNewAppName(e.target.value)}
                className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400">Categoria</label>
              <select
                value={newAppCategory}
                onChange={(e) => setNewAppCategory(e.target.value)}
                className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white"
              >
                <option value="Utilitários">Utilitários</option>
                <option value="Produtividade">Produtividade</option>
                <option value="Vendas & CRM">Vendas & CRM</option>
                <option value="Financeiro">Financeiro</option>
                <option value="Comunicação">Comunicação</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400">URL da Aplicação Web (Iframe)</label>
            <input
              type="url"
              placeholder="https://app.empresa.com.br"
              value={newAppIframeUrl}
              onChange={(e) => setNewAppIframeUrl(e.target.value)}
              className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white font-mono"
              required
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400">URL do Ícone (PNG / SVG)</label>
            <input
              type="url"
              placeholder="https://cdn.empresa.com/logo.png (Opcional)"
              value={newAppIconUrl}
              onChange={(e) => setNewAppIconUrl(e.target.value)}
              className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white font-mono"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200/50 dark:border-zinc-800/40">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowNewAppModal(false)
                setEditingApp(null)
              }}
              className="text-xs h-8 cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-[#004EB1] hover:bg-[#003d8d] text-white font-semibold text-xs cursor-pointer"
            >
              {editingApp ? "Salvar Alterações" : "Criar Aplicativo"}
            </Button>
          </div>
        </form>
      )}

      {/* Lista de Aplicativos Padrão e Customizados */}
      <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 font-sans">
        <CardContent className="p-0 divide-y divide-zinc-150 dark:divide-zinc-800">
          {installedApps.length === 0 && customAppsList.length === 0 ? (
            <div className="p-10 text-center flex flex-col items-center justify-center space-y-4">
              <div className="size-16 rounded-2xl bg-zinc-100 dark:bg-zinc-800/80 text-zinc-400 dark:text-zinc-500 flex items-center justify-center border border-zinc-200/60 dark:border-zinc-700/50 shadow-xs">
                {isCompanyAdmin ? (
                  <Grid className="size-8 text-[#004EB1] dark:text-blue-400 opacity-80" />
                ) : (
                  <Lock className="size-8 text-amber-500 dark:text-amber-400 opacity-80" />
                )}
              </div>

              <div className="max-w-md space-y-1.5">
                <h4 className="text-sm font-bold text-zinc-900 dark:text-white">
                  Nenhum aplicativo instalado
                </h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                  {isCompanyAdmin
                    ? "Esta empresa ainda não possui nenhum aplicativo instalado. Você pode explorar o Marketplace para instalar soluções corporativas ou cadastrar uma aplicação web customizada."
                    : "Nenhum aplicativo está instalado nesta empresa até o momento."}
                </p>
              </div>

              {isCompanyAdmin ? (
                <div className="flex flex-wrap items-center justify-center gap-2.5 pt-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      if (handleOpenMarketplaceTab) {
                        handleOpenMarketplaceTab()
                      } else {
                        setSettingsTab("marketplace")
                      }
                    }}
                    className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-3.5 flex items-center gap-1.5 cursor-pointer font-sans shadow-sm"
                  >
                    <Store className="size-3.5" />
                    <span>Explorar Marketplace</span>
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowNewAppModal(true)
                      setNewAppName("")
                      setNewAppCategory("Utilitários")
                      setNewAppIconUrl("https://cdn.boldworkspace.com.br/icons/app-default.png")
                      setNewAppIframeUrl("")
                      setEditingApp(null)
                    }}
                    className="text-xs h-8 px-3.5 flex items-center gap-1.5 cursor-pointer font-sans"
                  >
                    <Plus className="size-3.5" />
                    <span>Cadastrar App Customizado</span>
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-900/30 text-amber-800 dark:text-amber-300 text-xs font-sans max-w-md text-left mt-1">
                  <ShieldAlert className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <span>Solicite ao Administrador da Empresa para instalar novos aplicativos.</span>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Apps Padrão do Sistema / Marketplace Instalados */}
              {installedApps.map(({ app, status }) => {
                const isOfficial = app.type === "official"
                const canRename = app.type === "public" || app.type === "private"

                return (
                  <div key={app.id} className="p-4 flex items-center justify-between gap-4 hover:bg-zinc-50/50 dark:hover:bg-zinc-850/30 transition-colors">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={app.name} size="medium" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-zinc-900 dark:text-white flex items-center gap-2 flex-wrap">
                          <span className="truncate">{app.name}</span>
                          {isOfficial ? (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-[#004EB1] dark:bg-blue-950/40 dark:text-blue-400 font-extrabold uppercase shrink-0">
                              Oficial Bold
                            </span>
                          ) : app.type === "private" ? (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-600 dark:bg-purple-950/40 dark:text-purple-400 font-extrabold uppercase shrink-0">
                              Exclusivo
                            </span>
                          ) : app.category ? (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 font-extrabold uppercase shrink-0">
                              {app.category}
                            </span>
                          ) : null}
                        </p>
                        <p className="text-[10px] text-zinc-400 truncate">{app.description || "Aplicativo integrado ao workspace"}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {/* Botão de Editar Nome do Aplicativo no Workspace (apenas para apps public ou private) */}
                      {canRename && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenRenameModal(app.id, app.name, false)}
                          className="text-xs h-7 px-2.5 flex items-center gap-1.5 cursor-pointer text-zinc-700 dark:text-zinc-300 hover:text-[#004EB1] dark:hover:text-blue-400"
                          title="Editar nome exibido no workspace"
                        >
                          <Pencil className="size-3" />
                          <span>Editar Nome</span>
                        </Button>
                      )}

                      <div className="flex items-center gap-2 pl-2 border-l border-zinc-200 dark:border-zinc-800">
                        <span className={`text-[10px] font-bold ${status === "active" ? "text-emerald-600 dark:text-emerald-400" : "text-zinc-400 dark:text-zinc-500"}`}>
                          {status === "active" ? "Ativo" : "Inativo"}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleToggleAppStatus(app.id)}
                          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${status === "active" ? "bg-[#004EB1]" : "bg-zinc-200 dark:bg-zinc-800"
                            }`}
                          title={status === "active" ? "Desativar aplicativo" : "Ativar aplicativo"}
                        >
                          <span
                            className={`pointer-events-none inline-block size-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${status === "active" ? "translate-x-4" : "translate-x-0"
                              }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}

              {/* Apps Criados Manualmente / Customizados */}
              {customAppsList.map((app) => (
                <div key={app.id} className="p-4 flex items-center justify-between gap-4 bg-amber-500/5 dark:bg-amber-500/10 hover:bg-amber-500/10 dark:hover:bg-amber-500/15 transition-colors">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={app.name} size="medium" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-zinc-900 dark:text-white flex items-center gap-2 flex-wrap">
                        <span className="truncate">{app.name}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-400 font-extrabold uppercase shrink-0">Customizado</span>
                      </p>
                      <p className="text-[10px] text-zinc-400 font-mono truncate max-w-xs">{app.iframeUrl}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenRenameModal(app.id, app.name, true)}
                      className="text-xs h-7 px-2.5 flex items-center gap-1.5 cursor-pointer text-zinc-700 dark:text-zinc-300 hover:text-[#004EB1] dark:hover:text-blue-400"
                      title="Editar nome"
                    >
                      <Pencil className="size-3" />
                      <span>Editar Nome</span>
                    </Button>

                    {isCompanyAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingApp(app)
                          setNewAppName(app.name)
                          setNewAppCategory(app.category || "Utilitários")
                          setNewAppIconUrl(app.iconUrl || "")
                          setNewAppIframeUrl(app.iframeUrl || "")
                          setShowNewAppModal(false)
                        }}
                        className="text-xs h-7 px-2.5 cursor-pointer"
                      >
                        Configurar
                      </Button>
                    )}

                    {isCompanyAdmin && (
                      <button
                        type="button"
                        onClick={() => setAppToDelete({ id: app.id, name: app.name, isCustom: true })}
                        className="text-red-500 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-955/20 cursor-pointer flex items-center gap-1 text-xs font-semibold"
                        title="Excluir aplicativo customizado"
                      >
                        <Trash2 className="size-4" />
                        <span>Excluir</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de Edição de Nome do Aplicativo */}
      {appToRename && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-[#004EB1] dark:text-blue-400 flex items-center justify-center shrink-0 border border-blue-200/60 dark:border-blue-900/40">
                <Pencil className="size-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Renomear Aplicativo</h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Defina o nome exibido no workspace</p>
              </div>
            </div>

            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
              O novo nome definido abaixo será o nome oficial de exibição para todos os usuários membros deste workspace.
            </p>

            <form onSubmit={handleSaveRename} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                  Nome do Aplicativo
                </label>
                <input
                  type="text"
                  value={renameInputValue}
                  onChange={(e) => setRenameInputValue(e.target.value)}
                  placeholder="Nome do aplicativo..."
                  className="w-full px-3.5 py-2 text-xs rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1]"
                  autoFocus
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-zinc-150 dark:border-zinc-800">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setAppToRename(null)}
                  className="text-xs h-8 cursor-pointer font-sans"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-4 font-bold cursor-pointer font-sans shadow-sm"
                >
                  Salvar Nome
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Desinstalação de Aplicativo */}
      {appToDelete && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                <Trash2 className="size-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-zinc-900 dark:text-white">
                  {appToDelete.isCustom ? "Excluir Aplicativo" : "Desinstalar Aplicativo"}
                </h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Confirmar a desinstalação</p>
              </div>
            </div>

            <p className="text-xs text-zinc-600 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-950 p-3 rounded-lg border border-zinc-150 dark:border-zinc-800 leading-relaxed">
              Tem certeza que deseja desinstalar o aplicativo <strong className="text-zinc-900 dark:text-white">{appToDelete.name}</strong> do workspace? {!appToDelete.isCustom && "Ele continuará disponível no Marketplace para ser reinstalado quando você quiser."}
            </p>

            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAppToDelete(null)}
                className="text-xs h-8 cursor-pointer font-sans"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  if (appToDelete.isCustom) {
                    setCustomAppsList((prev) => prev.filter((a) => a.id !== appToDelete.id))
                  }
                  handleUninstallApp(appToDelete.id)
                  toast.success(
                    appToDelete.isCustom
                      ? `Aplicativo "${appToDelete.name}" excluído com sucesso!`
                      : `Aplicativo "${appToDelete.name}" desinstalado. Você pode reinstalá-lo pelo Marketplace.`
                  )
                  setAppToDelete(null)
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 px-4 font-semibold cursor-pointer font-sans"
              >
                {appToDelete.isCustom ? "Confirmar Exclusão" : "Confirmar Desinstalação"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </TabsContent>
  )
}
